import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { IDashboard } from '@/lib/types';
import api, { endpoints } from '@/services/api';
import { getApiErrorMessage } from '@/lib/utils';
import type { DashboardGridItem } from '@/store/slices/dashboardEditSlice';

export interface DashboardTemplateMeta {
  id: string;
  name: string;
  description: string;
  emoji: string;
  version: number;
  category: string;
  widgetCount: number;
  widgets?: Array<{
    title: string;
    kind: string;
    descriptorId?: string;
    descriptorKey?: string;
    type?: string;
    metric?: string;
    x?: number;
    y?: number;
    w?: number;
    h?: number;
  }>;
}

export const HOME_MIRROR_TEMPLATE_ID = 'home-mirror';

interface HomeMirrorBaselineWidget {
  id: string;
  type: string;
  title: string;
  x: number;
  y: number;
  w: number;
  h: number;
  dataSource: string;
  metric: string;
  filters?: Record<string, unknown>;
  config?: Record<string, unknown>;
}

const HOME_MIRROR_BASELINE_WIDGETS: readonly HomeMirrorBaselineWidget[] = [
  { id: 'hm-net-worth-kpi', type: 'kpi', title: 'Net Worth', x: 0, y: 0, w: 3, h: 2, dataSource: 'accounts', metric: 'netWorth' },
  { id: 'hm-income-kpi', type: 'kpi', title: 'Monthly Income', x: 3, y: 0, w: 3, h: 2, dataSource: 'transactions', metric: 'totalIncome' },
  { id: 'hm-expenses-kpi', type: 'kpi', title: 'Monthly Expenses', x: 6, y: 0, w: 3, h: 2, dataSource: 'transactions', metric: 'totalExpenses' },
  { id: 'hm-savings-rate-kpi', type: 'kpi', title: 'Savings Rate', x: 9, y: 0, w: 3, h: 2, dataSource: 'transactions', metric: 'savingsRate' },
  { id: 'hm-recent-transactions-list', type: 'table', title: 'Recent Transactions', x: 0, y: 2, w: 6, h: 5, dataSource: 'transactions', metric: 'recentTransactions', filters: { limit: 5 } },
  { id: 'hm-transaction-insights', type: 'area', title: 'Transaction Insights', x: 6, y: 2, w: 6, h: 5, dataSource: 'transactions', metric: 'spendingTrend', config: { months: 6 } },
  { id: 'hm-cashflow-composed', type: 'area', title: 'Daily Cashflow (30d)', x: 0, y: 7, w: 6, h: 4, dataSource: 'analytics', metric: 'dailyCashflow', config: { days: 30 } },
  { id: 'hm-spending-by-category-donut', type: 'donut', title: 'Spending by Category', x: 6, y: 7, w: 6, h: 4, dataSource: 'transactions', metric: 'categoryBreakdown', config: { limit: 8 } },
  { id: 'hm-budget-progress-list', type: 'progress', title: 'Budget Progress', x: 0, y: 11, w: 12, h: 4, dataSource: 'budgets', metric: 'budgetPerformance' },
  { id: 'hm-goals-progress-list', type: 'progress', title: 'Goal Progress', x: 0, y: 15, w: 6, h: 4, dataSource: 'goals', metric: 'goalProgress' },
  { id: 'hm-upcoming-bills-list', type: 'table', title: 'Upcoming Bills', x: 6, y: 15, w: 6, h: 4, dataSource: 'bills', metric: 'upcomingBills', config: { days: 30 } },
];

type DashboardTemplateLineageCarrier = Pick<IDashboard, 'sourceTemplateId' | 'fromTemplate'> | null | undefined;
type HomeMirrorPristineCandidate = Pick<IDashboard, 'sourceTemplateId' | 'fromTemplate' | 'widgets'> | null | undefined;

function normaliseForStableStringify(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(normaliseForStableStringify);
  }

  if (value && typeof value === 'object') {
    const input = value as Record<string, unknown>;
    return Object.keys(input).sort().reduce<Record<string, unknown>>((acc, key) => {
      acc[key] = normaliseForStableStringify(input[key]);
      return acc;
    }, {});
  }

  return value;
}

function stableStringify(value: unknown): string {
  return JSON.stringify(normaliseForStableStringify(value ?? null));
}

function isBaselineWidgetUnchanged(
  widget: IDashboard['widgets'][number] | undefined,
  baseline: HomeMirrorBaselineWidget,
): boolean {
  if (!widget) return false;

  return widget.id === baseline.id
    && widget.type === baseline.type
    && widget.title === baseline.title
    && widget.x === baseline.x
    && widget.y === baseline.y
    && widget.w === baseline.w
    && widget.h === baseline.h
    && widget.dataSource === baseline.dataSource
    && widget.metric === baseline.metric
    && stableStringify(widget.filters) === stableStringify(baseline.filters)
    && stableStringify(widget.config) === stableStringify(baseline.config);
}

export function getDashboardSourceTemplateId(
  dashboard: DashboardTemplateLineageCarrier,
): string | undefined {
  return dashboard?.sourceTemplateId ?? dashboard?.fromTemplate?.templateId;
}

export function isHomeMirrorDashboard(
  dashboard: DashboardTemplateLineageCarrier,
): boolean {
  return getDashboardSourceTemplateId(dashboard) === HOME_MIRROR_TEMPLATE_ID;
}

export function isPristineHomeMirrorDashboard(
  dashboard: HomeMirrorPristineCandidate,
): boolean {
  if (!isHomeMirrorDashboard(dashboard)) return false;
  if (!dashboard?.widgets || dashboard.widgets.length !== HOME_MIRROR_BASELINE_WIDGETS.length) {
    return false;
  }

  const widgetsById = new Map(dashboard.widgets.map((widget) => [widget.id, widget]));
  return HOME_MIRROR_BASELINE_WIDGETS.every((baseline) =>
    isBaselineWidgetUnchanged(widgetsById.get(baseline.id), baseline),
  );
}

function normaliseDashboard(dashboard: IDashboard): IDashboard {
  const sourceTemplateId = getDashboardSourceTemplateId(dashboard);

  if (!sourceTemplateId || dashboard.sourceTemplateId === sourceTemplateId) {
    return dashboard;
  }

  return {
    ...dashboard,
    sourceTemplateId,
  };
}

function normaliseDashboards(dashboards: IDashboard[]): IDashboard[] {
  return dashboards.map(normaliseDashboard);
}

interface DashboardsState {
  dashboards: IDashboard[];
  dashboardTemplates: DashboardTemplateMeta[];
  currentDashboard: IDashboard | null;
  widgetData: Record<string, unknown>;
  isLoading: boolean;
  isDataLoading: boolean;
  isTemplatesLoading: boolean;
  error: string | null;
}

const initialState: DashboardsState = {
  dashboards: [],
  dashboardTemplates: [],
  currentDashboard: null,
  widgetData: {},
  isLoading: false,
  isDataLoading: false,
  isTemplatesLoading: false,
  error: null,
};

export const fetchDashboards = createAsyncThunk(
  'dashboards/fetchDashboards',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.dashboards.list);
      return (response.data.data || response.data) as IDashboard[];
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch dashboards'));
    }
  }
);

export const fetchDashboard = createAsyncThunk(
  'dashboards/fetchDashboard',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.dashboards.get(id));
      return (response.data.data || response.data) as IDashboard;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch dashboard'));
    }
  }
);

export const fetchDashboardTemplates = createAsyncThunk(
  'dashboards/fetchDashboardTemplates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.dashboards.templates);
      return (response.data.data || response.data) as DashboardTemplateMeta[];
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch dashboard templates'));
    }
  }
);

export const createDashboard = createAsyncThunk(
  'dashboards/createDashboard',
  async (data: Partial<IDashboard>, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.dashboards.create, data);
      return (response.data.data || response.data) as IDashboard;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create dashboard'));
    }
  }
);

export const createDashboardFromTemplate = createAsyncThunk(
  'dashboards/createDashboardFromTemplate',
  async (templateId: string, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.dashboards.fromTemplate(templateId));
      return (response.data.data || response.data) as { id: string };
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create dashboard from template'));
    }
  }
);

export const cloneDashboard = createAsyncThunk(
  'dashboards/cloneDashboard',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.dashboards.clone(id));
      return (response.data.data || response.data) as { id: string; sourceTemplateId?: string };
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to clone dashboard'));
    }
  }
);

export const updateDashboard = createAsyncThunk(
  'dashboards/updateDashboard',
  async ({ id, data }: { id: string; data: Partial<IDashboard> }, { rejectWithValue }) => {
    try {
      const response = await api.put(endpoints.dashboards.update(id), data);
      return (response.data.data || response.data) as IDashboard;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update dashboard'));
    }
  }
);

export const deleteDashboard = createAsyncThunk(
  'dashboards/deleteDashboard',
  async (id: string, { rejectWithValue }) => {
    try {
      await api.delete(endpoints.dashboards.delete(id));
      return id;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete dashboard'));
    }
  }
);

export const fetchDashboardData = createAsyncThunk(
  'dashboards/fetchDashboardData',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.dashboards.data(id));
      // Backend wraps each widget as { data: resolverResult } inside
      // { dashboardId, widgets: { wId: { data: X } } }.
      // Flatten to { wId: X } so callers can do widgetData[widget.id].
      const payload = (response.data.data ?? response.data) as {
        widgets?: Record<string, { data?: unknown; error?: string; status?: string }>;
      };
      const raw = payload.widgets ?? {};
      return Object.fromEntries(
        Object.entries(raw).map(([wid, v]) => [wid, v?.data ?? null])
      ) as Record<string, unknown>;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch dashboard data'));
    }
  }
);

const dashboardsSlice = createSlice({
  name: 'dashboards',
  initialState,
  reducers: {
    clearCurrentDashboard(state) {
      state.currentDashboard = null;
      state.widgetData = {};
    },
    setCurrentDashboard(state, action) {
      state.currentDashboard = normaliseDashboard(action.payload as IDashboard);
    },
    applyPresetLayoutToCurrentDashboard(state, action: { payload: DashboardGridItem[] }) {
      if (!state.currentDashboard) return;

      const byId = new Map(action.payload.map((item) => [item.i, item]));
      state.currentDashboard.widgets = state.currentDashboard.widgets.map((widget) => {
        const layout = byId.get(widget.id);
        if (!layout) return widget;
        return {
          ...widget,
          x: layout.x,
          y: layout.y,
          w: layout.w,
          h: layout.h,
        };
      });
    },
  },
  extraReducers: (builder) => {
    // fetchDashboards
    builder.addCase(fetchDashboards.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchDashboards.fulfilled, (state, action) => {
      state.isLoading = false;
      state.dashboards = normaliseDashboards(action.payload);
    });
    builder.addCase(fetchDashboards.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // fetchDashboard
    builder.addCase(fetchDashboard.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchDashboard.fulfilled, (state, action) => {
      state.isLoading = false;
      state.currentDashboard = normaliseDashboard(action.payload);
    });
    builder.addCase(fetchDashboard.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // fetchDashboardTemplates
    builder.addCase(fetchDashboardTemplates.pending, (state) => {
      state.isTemplatesLoading = true;
      state.error = null;
    });
    builder.addCase(fetchDashboardTemplates.fulfilled, (state, action) => {
      state.isTemplatesLoading = false;
      state.dashboardTemplates = action.payload;
    });
    builder.addCase(fetchDashboardTemplates.rejected, (state, action) => {
      state.isTemplatesLoading = false;
      state.error = action.payload as string;
    });

    // createDashboard
    builder.addCase(createDashboard.fulfilled, (state, action) => {
      const createdDashboard = normaliseDashboard(action.payload);
      state.dashboards.push(createdDashboard);
      state.currentDashboard = createdDashboard;
    });

    // updateDashboard
    builder.addCase(updateDashboard.fulfilled, (state, action) => {
      const updatedDashboard = normaliseDashboard(action.payload);
      const idx = state.dashboards.findIndex((d) => d._id === updatedDashboard._id);
      if (idx >= 0) state.dashboards[idx] = updatedDashboard;
      state.currentDashboard = updatedDashboard;
    });

    // deleteDashboard
    builder.addCase(deleteDashboard.fulfilled, (state, action) => {
      state.dashboards = state.dashboards.filter((d) => d._id !== action.payload);
      if (state.currentDashboard?._id === action.payload) {
        state.currentDashboard = null;
      }
    });

    // fetchDashboardData
    builder.addCase(fetchDashboardData.pending, (state) => {
      state.isDataLoading = true;
    });
    builder.addCase(fetchDashboardData.fulfilled, (state, action) => {
      state.isDataLoading = false;
      state.widgetData = action.payload;
    });
    builder.addCase(fetchDashboardData.rejected, (state) => {
      state.isDataLoading = false;
    });
  },
});

export const { clearCurrentDashboard, setCurrentDashboard, applyPresetLayoutToCurrentDashboard } = dashboardsSlice.actions;
export default dashboardsSlice.reducer;
